/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/** @jsx jsx */
import { jsx } from '@emotion/react';
import { useState } from 'react';
import starterProjects from '../../../@generated/starter-projects.json';
import { colors, shadows } from '../../theme';
import { Link } from '../Link/Link';
import Badge from '../MdxComponents/badge';
import CopyToClipboardSvg from '../../../static/icons/CopyToClipboard';
import uniq from 'lodash/uniq';
import { copyToClipboard } from '../MdxComponents/codeBlock';
import { onSm } from '../../styles/responsive';

const codeBlockColor = '#bab9b9';
const allTags = uniq(starterProjects.map((example) => example.tags).flat()).sort();
const allUniqueResources = uniq(
  starterProjects
    .map((example) => example.usedResources)
    .flat()
    .sort()
);

const getInitCommand = (id: string) => `stacktape init --project-id ${id}`;

const cardWidth = 375;
const cardHeight = 353;

const ExampleCard = ({ example }: { example: typeof starterProjects[0] }) => {
  return (
    <div
      css={{
        background: colors.darkerBackground,
        boxShadow: shadows.boxShadow,
        position: 'relative',
        borderRadius: '8px',
        padding: '16px',
        fontSize: '15px',
        flexBasis: '40%',
        height: `${cardHeight}px`,
        minHeight: `${cardHeight}px`,
        margin: '7px 10px 10px 5px',
        width: `${cardWidth}px`,
        maxWidth: `${cardWidth}px`,
        minWidth: `${cardWidth}px`,
        [onSm]: {
          width: '320px',
          maxWidth: '320px',
          minWidth: '320px',
          height: '398px',
          minHeight: '398px'
        }
      }}
    >
      <p
        css={{
          fontWeight: 'bold',
          fontSize: '20px',
          margin: '0',
          textAlign: 'left',
          height: '45px'
        }}
      >
        {example.name
          .split('-')
          .map((word) => `${word[0].toUpperCase()}${word.slice(1)}`)
          .join(' ')}
      </p>
      <div css={{ padding: '13px 0px', lineHeight: 1.8 }}>
        {example.tags.map((tag) => (
          <Badge backgroundColor={colors.primary}>{tag}</Badge>
        ))}
      </div>
      <p
        css={{
          fontSize: '15px',
          lineHeight: 1.4,
          margin: '0'
        }}
      >
        {example.shortDescription}
      </p>
      <div
        css={{
          fontSize: '14px',
          lineHeight: 1.5,
          margin: '0',
          border: `1.5px solid ${codeBlockColor}`,
          color: codeBlockColor,
          padding: '4px 5px 0px 5px',
          borderRadius: '4px',
          position: 'absolute',
          bottom: '47px',
          marginRight: '15px',
          height: '56px',
          width: 'calc(100% - 34px)',
          [onSm]: {
            height: '84px'
          }
        }}
      >
        <span css={{ fontFamily: 'monospace', letterSpacing: 0.2 }}>{getInitCommand(example.id)}</span>
        <span
          css={{ cursor: 'pointer', position: 'absolute', bottom: '0px', right: '7px' }}
          onClick={() => copyToClipboard(getInitCommand(example.id))}
        >
          <CopyToClipboardSvg />
        </span>
      </div>
      <Link css={{ position: 'absolute', bottom: '17px' }} to={example.githubLink}>
        Github
      </Link>
    </div>
  );
};

const starterProjectsList = () => {
  const [activeTags, setActiveTags] = useState([]);
  const getSetActiveHandler = (tag: string) => () => {
    if (activeTags.includes(tag)) {
      setActiveTags(activeTags.filter((t) => t !== tag));
    } else {
      setActiveTags(activeTags.concat(tag));
    }
  };
  const [activeUsedResources, setActiveUsedResources] = useState([]);
  const getSetActiveUsedResourcesHandler = (resource: string) => () => {
    if (activeUsedResources.includes(resource)) {
      setActiveUsedResources(activeUsedResources.filter((r) => r !== resource));
    } else {
      setActiveUsedResources(activeUsedResources.concat(resource));
    }
  };

  const starterProjectsToShow = starterProjects.filter(
    (example) =>
      (!activeTags.length || activeTags.every((tag) => example.tags.includes(tag))) &&
      (!activeUsedResources.length || activeUsedResources.every((resource) => example.usedResources.includes(resource)))
  );

  return (
    <div css={{ marginTop: '10px' }}>
      <div css={{ display: 'flex', justifyContent: 'space-between' }}>
        <p css={{ fontWeight: 'bold', marginTop: '15px' }}>Filter by category</p>
      </div>
      <div css={{ padding: '10px 0px', lineHeight: 1.8, userSelect: 'none' }}>
        {allTags.map((tag, idx) => {
          return (
            <Badge
              key={idx}
              onClick={getSetActiveHandler(tag)}
              backgroundColor={activeTags.includes(tag) ? colors.primary : colors.background}
              border={`1px solid ${codeBlockColor}`}
              fontSize="15px"
            >
              {tag}
            </Badge>
          );
        })}
      </div>
      <p css={{ fontWeight: 'bold', marginTop: '15px' }}>Filter by used resources</p>
      <div css={{ padding: '10px 0px', lineHeight: 1.8 }}>
        {allUniqueResources.map((resource, idx) => {
          return (
            <Badge
              key={idx}
              onClick={getSetActiveUsedResourcesHandler(resource)}
              backgroundColor={activeUsedResources.includes(resource) ? colors.primary : colors.background}
              border={`1px solid ${codeBlockColor}`}
              fontSize="15px"
            >
              {resource}
            </Badge>
          );
        })}
      </div>
      <div
        css={{
          marginTop: '18px',
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          // alignItems: 'flex-start',
          justifyContent: 'center'
        }}
      >
        {starterProjectsToShow.map((example, idx) => (
          <ExampleCard key={idx} example={example} />
        ))}
        {!starterProjectsToShow.length ? (
          <p css={{ margin: '28px 0px', justifyContent: 'center' }}>No starter projects match selected categories.</p>
        ) : null}
      </div>
    </div>
  );
};

export default starterProjectsList;
